import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";

const isOccupied = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return value === true || value === 1 || normalized === "1" || normalized === "true" || normalized === "t";
};

const getRentalTypeOptionsFromCategory = (category) => {
  const normalized = String(category || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

  const hasResidential = /\bres(idential)?\b/.test(normalized) || normalized.includes("residential");
  const hasCommercial =
    /\bcom(mercial|merce|ercial)?\b/.test(normalized) ||
    normalized.includes("commercial") ||
    normalized.includes("commerce") ||
    normalized.includes("comercial");

  const mixedHint =
    normalized.includes("mixed") ||
    normalized.includes("both") ||
    normalized.includes("/") ||
    normalized.includes("&") ||
    normalized.includes(" and ");

  if ((hasResidential && hasCommercial) || (mixedHint && (hasResidential || hasCommercial))) {
    return ["Residential", "Commercial"];
  }
  if (hasResidential) return ["Residential"];
  if (hasCommercial) return ["Commercial"];

  // Fallback for unknown categories.
  return ["Residential", "Commercial"];
};

const idsMatch = (left, right) => String(left ?? "") === String(right ?? "");
const resolveAssetCategory = (asset) => asset?.category || asset?.assetType || asset?.type || "";

const Rentals = () => {
  const { toast } = useStore();

  const [rentals, setRentals] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedAssetCategory, setSelectedAssetCategory] = useState("");

  const [form, setForm] = useState({
    assetId: "",
    location: "",
    rentalType: "Residential",
    unitNumber: "",
    billingPeriod: "Monthly",
    description: "",
    rentalCost: "",
    occupied: false,
    tenantId: "",
    startDate: "",
    endDate: ""
  });

  const selectedAsset = assets.find((asset) => idsMatch(asset.id, form.assetId));
  const effectiveCategory = selectedAssetCategory || resolveAssetCategory(selectedAsset);
  const rentalTypeOptions = getRentalTypeOptionsFromCategory(effectiveCategory);
  const showRentalTypeDropdown = Boolean(selectedAsset) && rentalTypeOptions.length > 1;

  const fetchRentals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rentals');
      setRentals(res.data.data || []);
    } catch (error) {
      console.error('Error fetching rentals:', error);
      toast('Failed to load rentals', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await api.get('/assets');
      setAssets(res.data.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
    fetchAssets();
    fetchTenants();
  }, [fetchRentals, fetchAssets, fetchTenants]);

  const handleAssetChange = async (e) => {
    const assetId = e.target.value;
    let asset = assets.find((a) => idsMatch(a.id, assetId));

    if (assetId) {
      try {
        const res = await api.get(`/assets/${assetId}`);
        const detailedAsset = res.data?.data;
        if (detailedAsset) {
          asset = { ...asset, ...detailedAsset };
          setAssets((prev) => {
            const idx = prev.findIndex((a) => idsMatch(a.id, assetId));
            if (idx === -1) return [...prev, asset];
            const next = [...prev];
            next[idx] = { ...next[idx], ...asset };
            return next;
          });
        }
      } catch (error) {
        console.error('Error loading asset details:', error);
      }
    }

    const options = getRentalTypeOptionsFromCategory(resolveAssetCategory(asset));
    setSelectedAssetCategory(resolveAssetCategory(asset));

    setForm((prev) => ({
      ...prev,
      assetId,
      location: asset?.location || "",
      rentalType: options.includes(prev.rentalType) ? prev.rentalType : options[0]
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });

    if (name === 'occupied' && !checked) {
      setForm(prev => ({
        ...prev,
        occupied: false,
        tenantId: "",
        startDate: "",
        endDate: ""
      }));
    }
  };

  const clearForm = () => {
    setForm({
      assetId: "",
      location: "",
      rentalType: "Residential",
      unitNumber: "",
      billingPeriod: "Monthly",
      description: "",
      rentalCost: "",
      occupied: false,
      tenantId: "",
      startDate: "",
      endDate: ""
    });
    setSelectedRental(null);
    setSelectedAssetCategory("");
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm({
      assetId: "",
      location: "",
      rentalType: "Residential",
      unitNumber: "",
      billingPeriod: "Monthly",
      description: "",
      rentalCost: "",
      occupied: false,
      tenantId: "",
      startDate: "",
      endDate: ""
    });
    setSelectedRental(null);
    setSelectedAssetCategory("");
    setFormOpen(true);
  };

  useEffect(() => {
    if (!form.assetId) return;

    setForm((prev) => {
      const currentAsset = assets.find((asset) => idsMatch(asset.id, prev.assetId));
      const options = getRentalTypeOptionsFromCategory(resolveAssetCategory(currentAsset));

      if (options.includes(prev.rentalType)) return prev;
      return { ...prev, rentalType: options[0] };
    });
  }, [assets, form.assetId]);

  useEffect(() => {
    if (!form.assetId) {
      if (selectedAssetCategory) setSelectedAssetCategory("");
      return;
    }

    const currentAsset = assets.find((asset) => idsMatch(asset.id, form.assetId));
    const category = resolveAssetCategory(currentAsset);
    if (category && category !== selectedAssetCategory) {
      setSelectedAssetCategory(category);
    }
  }, [assets, form.assetId, selectedAssetCategory]);

  const handleAdd = async () => {
    if (!form.assetId) {
      toast('Please select an asset', 'error');
      return;
    }

    if (!form.unitNumber || !form.unitNumber.trim()) {
      toast('Unit number is required', 'error');
      return;
    }

    if (!form.rentalCost || parseFloat(form.rentalCost) <= 0) {
      toast('Valid rental cost is required', 'error');
      return;
    }

    if (form.occupied && !form.tenantId) {
      toast('Please select a tenant for occupied rental', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        assetId: parseInt(form.assetId),
        unitNumber: form.unitNumber.trim(),
        rentalType: form.rentalType,
        billingPeriod: form.billingPeriod,
        rentalCost: parseFloat(form.rentalCost),
        description: form.description?.trim() || null,
        occupied: form.occupied ? 1 : 0,
        tenantId: form.occupied && form.tenantId ? parseInt(form.tenantId) : null,
        startDate: form.occupied && form.startDate ? form.startDate : null,
        endDate: form.occupied && form.endDate ? form.endDate : null
      };

      const response = await api.post('/rentals', payload);
      toast(response.data?.message || 'Rental added successfully', 'success');
      clearForm();
      fetchRentals();
    } catch (error) {
      console.error('Error adding rental:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add rental';
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedRental) {
      toast('Please select a rental to update', 'error');
      return;
    }

    if (!form.rentalCost || parseFloat(form.rentalCost) <= 0) {
      toast('Valid rental cost is required', 'error');
      return;
    }

    if (form.occupied && !form.tenantId) {
      toast('Please select a tenant for occupied rental', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        unitNumber: form.unitNumber.trim(),
        rentalType: form.rentalType,
        billingPeriod: form.billingPeriod,
        rentalCost: parseFloat(form.rentalCost),
        description: form.description?.trim() || null,
        occupied: form.occupied ? 1 : 0,
        tenantId: form.occupied && form.tenantId ? parseInt(form.tenantId) : null,
        startDate: form.occupied && form.startDate ? form.startDate : null,
        endDate: form.occupied && form.endDate ? form.endDate : null
      };

      await api.put(`/rentals/${selectedRental.id}`, payload);
      toast('Rental updated successfully', 'success');
      clearForm();
      fetchRentals();
    } catch (error) {
      console.error('Error updating rental:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update rental';
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await api.delete(`/rentals/${deleteTarget.id}`);
      toast('Rental deleted successfully', 'success');
      setDeleteTarget(null);
      fetchRentals();
    } catch (error) {
      console.error('Error deleting rental:', error);
      toast(error.response?.data?.message || 'Failed to delete rental', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (rental) => {
    setSelectedRental(rental);
    setFormOpen(true);
    const currentAsset = assets.find((asset) => idsMatch(asset.id, rental.assetId));
    setSelectedAssetCategory(resolveAssetCategory(currentAsset));
    setForm({
      assetId: rental.assetId?.toString() || "",
      location: rental.location || "",
      rentalType: rental.rentalType || "Residential",
      unitNumber: rental.unitNumber || "",
      billingPeriod: rental.billingPeriod || "Monthly",
      description: rental.description || "",
      rentalCost: rental.rentalCost?.toString() || "",
      occupied: isOccupied(rental.occupied),
      tenantId: rental.tenantId?.toString() || "",
      startDate: rental.startDate ? rental.startDate.split('T')[0] : "",
      endDate: rental.endDate ? rental.endDate.split('T')[0] : ""
    });
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return '-';
    const tenant = tenants.find((t) => idsMatch(t.id, tenantId));
    return tenant ? tenant.tenantName : '-';
  };

  const filtered = rentals.filter(rental =>
    (rental.assetName || '').toLowerCase().includes(search.toLowerCase()) ||
    (rental.unitNumber || '').toLowerCase().includes(search.toLowerCase()) ||
    (rental.location || '').toLowerCase().includes(search.toLowerCase()) ||
    (rental.rentalType || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Rentals"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Rental
          </Button>
        }
      />

      <DataTable
        columns={[
          { key: "assetName", label: "Asset Name" },
          { key: "location", label: "Location" },
          { key: "unitNumber", label: "Unit Number" },
          {
            key: "tenantName",
            label: "Tenant",
            sortValue: (row) => getTenantName(row.tenantId),
            render: (row) => getTenantName(row.tenantId)
          },
          { key: "rentalType", label: "Rental Type" },
          {
            key: "rentalCost",
            label: "Cost",
            sortValue: (row) => parseFloat(row.rentalCost || 0),
            render: (row) => `GMD ${parseFloat(row.rentalCost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
          },
          {
            key: "occupied",
            label: "Occupied",
            render: (row) =>
              isOccupied(row.occupied) ? (
                <span className="text-green-600 font-semibold">YES</span>
              ) : (
                <span className="text-gray-500 font-semibold">NO</span>
              )
          }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by asset, unit number, location or type..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage='No rentals found. Click "Add Rental" to create one.'
      />

      <Modal
        open={formOpen}
        title={selectedRental ? "Update Rental" : "Add Rental"}
        onClose={clearForm}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={clearForm} disabled={loading}>
              Cancel
            </Button>
            {selectedRental ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={LABEL_CLASS}>
              Asset Name <span className="text-red-500">*</span>
            </label>
            <select
              name="assetId"
              value={form.assetId}
              onChange={handleAssetChange}
              disabled={selectedRental !== null}
              className={FIELD_CLASS}
            >
              <option value="">Select asset</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.assetName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>Rental Type</label>
            <select
              name="rentalType"
              value={form.rentalType}
              onChange={handleChange}
              disabled={!selectedAsset || !showRentalTypeDropdown}
              className={FIELD_CLASS}
            >
              {!selectedAsset ? (
                <option value="">Select asset first</option>
              ) : (
                rentalTypeOptions.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>Location</label>
            <input type="text" name="location" value={form.location} disabled className={FIELD_CLASS} />
          </div>

          <div>
            <label className={LABEL_CLASS}>Billing Method</label>
            <select name="billingPeriod" value={form.billingPeriod} onChange={handleChange} className={FIELD_CLASS}>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Semi-Annual">Semi-Annual</option>
              <option value="Annual">Annual</option>
              <option value="One Time">One Time</option>
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>
              Unit Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="unitNumber"
              value={form.unitNumber}
              onChange={handleChange}
              placeholder="Enter unit number"
              className={FIELD_CLASS}
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>
              Cost <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="rentalCost"
              value={form.rentalCost}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Enter cost"
              className={FIELD_CLASS}
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Enter description (optional)"
              className={`${FIELD_CLASS} resize-none`}
            />
          </div>

          <div className="space-y-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="occupied"
                checked={form.occupied}
                onChange={handleChange}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label className="ml-3 text-sm font-semibold text-gray-700">Occupied</label>
            </div>

            {form.occupied && (
              <>
                <div>
                  <label className={LABEL_CLASS}>
                    Tenant Name <span className="text-red-500">*</span>
                  </label>
                  <select name="tenantId" value={form.tenantId} onChange={handleChange} className={FIELD_CLASS}>
                    <option value="">Select tenant</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.tenantName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Contract Start Date</label>
                  <input type="date" name="startDate" value={form.startDate} onChange={handleChange} className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Contract End Date</label>
                  <input type="date" name="endDate" value={form.endDate} onChange={handleChange} className={FIELD_CLASS} />
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={deleteTarget ? `Are you sure you want to delete rental unit "${deleteTarget.unitNumber}"?` : ""}
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Rentals;